import AnimateOnEnter from "@/ui/animations/AnimateOnEnter"
import CtaLink2 from "@/ui/atoms/CtaLink2"
import Typography from "@/ui/atoms/Typography"
import Container from "@/ui/structure/Container"
import PagePadding from "@/ui/structure/PagePadding"
import { CtaData } from "@/ui/types/cta"
import { setCssVar } from "@/ui/utils/styles"
import { classNames, setColorVar, ThemeColor } from "@punks/ui-core"
import { ReactNode, useRef } from "react"

export type Hero2Classes = {
  content?: string
  title?: string
  subtitle?: string
}

export type Hero2BackgroundColor = {
  color?: ThemeColor
  opacity?: number
}

export type Hero2Background = {
  image?: ReactNode
  color?: Hero2BackgroundColor
}

export type HeroAlignment = "left" | "center" | "right"

export interface Hero2Props {
  classes?: Hero2Classes
  background?: Hero2Background
  backgroundColor?: ThemeColor
  height?: string
  text?: ReactNode
  textColor?: ThemeColor
  cta?: CtaData[]
  alignment?: HeroAlignment
  decorations?: ReactNode[]
}

const Hero2 = ({
  classes,
  backgroundColor,
  background,
  textColor,
  height,
  text,
  cta,
  alignment,
  decorations,
}: Hero2Props) => {
  return (
    <div
      className="relative"
      style={{
        ...setCssVar("--h", height ?? "100vh"),
        ...setColorVar("--c", textColor),
        ...setColorVar("--bg", backgroundColor),
      }}
    >
      {decorations?.map((x, i) => (
        <AnimateOnEnter key={i} delay={250} animationType="fadeIn">
          <div className="absolute inset-0">{x}</div>
        </AnimateOnEnter>
      ))}
      <PagePadding className="pb-11 flex flex-col min-h-[var(--h)]">
        <div className="flex-1 relative flex items-center text-[var(--c)]">
          <div className="absolute inset-0 z-[-1]">
            {backgroundColor && (
              <div className="absolute inset-0 rounded-b-card bg-[var(--bg)]"></div>
            )}
            {background?.image && <>{background.image}</>}
          </div>
          <Container
            className={classNames(
              "flex flex-col pt-32 xl:pt-16",
              {
                "text-center items-center": alignment === "center",
                "text-center xl:text-left items-start": alignment === "left",
                "text-right items-end": alignment === "right",
              },
              classes?.content
            )}
          >
            {text && (
              <AnimateOnEnter className="xl:max-w-[800px]">
                <Typography
                  component="div"
                  variant={["h4", "h4", "h2", "h1"]}
                  weight={"bold"}
                  color={textColor}
                  className={classNames("my-8", classes?.subtitle)}
                >
                  {text}
                </Typography>
              </AnimateOnEnter>
            )}
            {cta?.length ? (
              <AnimateOnEnter
                delay={500}
                className="mx-auto mb-8 mt-16 xl:mx-0"
              >
                <div className="flex flex-col md:flex-row md:gap-4 items-center">
                  {cta?.map((x, i) => (
                    <CtaLink2
                      center
                      arrow
                      key={i}
                      {...x}
                      className="min-w-[240px] my-4 xl:my-8"
                    />
                  ))}
                </div>
              </AnimateOnEnter>
            ) : undefined}
          </Container>
        </div>
      </PagePadding>
    </div>
  )
}

export default Hero2
